import { useReactiveVar } from '@apollo/client'
import { TabItemConfig } from '@deltek/specification-client/core/Navigation/TopTabNav'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import Button from '@material-ui/core/Button'
import { makeStyles, Theme } from '@material-ui/core/styles'
import clsx from 'clsx'
import React from 'react'
import { NavLink, useLocation } from 'react-router-dom'

import {
    setActiveStudioHeaderTab,
    studioPage,
} from 'api/graphQL/reactiveVars/studio'
import { userInfo } from 'api/graphQL/reactiveVars/user'
import {
    isContentTeam,
    isOmWorkspaceUser,
} from 'api/graphQL/reactiveVars/workspaceContent'
import useArrowKeyNav from 'shared/hooks/useArrowKeyNav'
import { useHarmonyTabStyles } from 'shared/hooks/useHarmonyTabStyles'
import useIsHarmonyHeader from 'shared/hooks/useIsHarmonyHeader'

interface StyleProps {
    isBestPracticeEnabled?: boolean
}

const useStyles = makeStyles((theme: Theme) => ({
    tabButton: {
        fontWeight: 'normal',
        color: theme.palette.common.white,
        border: '1px solid white',
        borderRadius: theme.spacing(100),
        borderColor: theme.palette.common.white,
        '&:hover': {
            color: theme.palette.common.white,
        },
    },
    disabledTabButton: {
        '& span': {
            color: 'rgba(255, 255, 255, 0.25)',
        },
    },
    normalSizeTabs: {
        fontSize: pxToRem(18),
        padding: theme.spacing(0.5, 1),
        fontWeight: 400,
    },
    normalSizeTabsHarmony: {
        fontSize: pxToRem(16),
    },
    largeSizeTabs: {
        fontWeight: 400,
        fontSize: pxToRem(24),
        padding: theme.spacing(0.75, 2),
    },
    activeTabButton: {
        color: theme.palette.primary.main,
        backgroundColor: theme.palette.common.white,
        '&:hover': {
            backgroundColor: theme.palette.common.white,
            color: theme.palette.primary.main,
        },
    },

    backdrop: {
        zIndex: theme.zIndex.drawer + 1,
        color: theme.palette.common.white,
    },
    topNavButtons: {
        justifyContent: 'center',
        marginLeft: theme.spacing(0),
        '@media (max-width: 1445px)': {
            justifyContent: 'space-evenly',
            width: (props: StyleProps) =>
                props.isBestPracticeEnabled ? '60%' : '100%',
            margin: 'auto',
        },
        '& .MuiGrid-item': {
            padding: theme.spacing(0, 2),
            '@media (max-width: 1445px)': {
                padding: theme.spacing(0),
            },
        },
    },
    // Harmony's tabButtonLight already provides the full inter-tab gap via its own
    // !important padding — this GridItem padding is a classic-header-only value that
    // would otherwise stack on top of it and double the visible gap.
    topNavButtonsLight: {
        '& .MuiGrid-item': {
            padding: '0 !important',
        },
    },
}))

export enum StudioTabs {
    ElementLibrary = 'element-library',
    TaskManager = 'dashboard',
    WorkItemManager = 'workitems',
    Workspace = 'workspace',
    Import = 'import',
    Report = 'report',
}

export interface StudioTabItemProps extends TabItemConfig {
    link: { pathname: string; state: { isContentTeam: boolean } } | string
}

export const TabItems: StudioTabItemProps[] = [
    {
        label: 'Element Library',
        value: StudioTabs.ElementLibrary,
        link: '/master/element-library',
    },
    {
        label: 'Dashboard',
        value: StudioTabs.TaskManager,
        link: '/master/dashboard',
    },
    {
        label: 'Work Items',
        value: StudioTabs.WorkItemManager,
        link: '/workflow/workitems',
    },
    {
        label: 'Report',
        value: StudioTabs.Report,
        link: '/master/report',
    },
    {
        label: 'Import',
        value: StudioTabs.Import,
        link: '/master/import',
    },
]

export interface StudioTopTabNavProps {
    /**
     * Determines whether the component expands its horizontal spacing
     * to take up the full width of its container.
     */
    expandToHorizontalSpace?: boolean

    /**
     * Determines whether to use original tab font and background sizes
     * or the bigger variants.
     */
    useBiggerTabs?: boolean
}

const getActiveHeader = (path: string) => {
    return TabItems.find((tab: StudioTabItemProps) =>
        path.includes(tab.link as string)
    )?.value
}

const StudioTopTabNav: React.FC<StudioTopTabNavProps> = ({
    expandToHorizontalSpace = false,
    useBiggerTabs = false,
}) => {
    const { pathname } = useLocation()
    const isHarmonyHeader = useIsHarmonyHeader()
    const { containerRef, onKeyDown } = useArrowKeyNav<HTMLDivElement>('a')

    const { activeStudioHeaderTab } = useReactiveVar(studioPage)
    const { isBestPracticeEnabled, isInternalCmsUser } = useReactiveVar(
        userInfo
    )
    const isContentTeamUser = useReactiveVar(isContentTeam)
    const isAeUser = useReactiveVar(isOmWorkspaceUser)
    const classes = useStyles({
        isBestPracticeEnabled,
    })
    const harmonyClasses = useHarmonyTabStyles()
    const disabledTabButtonClassName = isHarmonyHeader
        ? harmonyClasses.disabledTabButtonLight
        : classes.disabledTabButton
    const handleActiveTabSelection = React.useCallback(() => {
        setActiveStudioHeaderTab(getActiveHeader(pathname))
    }, [pathname])
    const isTabDisplayed = React.useCallback(
        (tabItem) => {
            if (tabItem.value === StudioTabs.Import && !isBestPracticeEnabled) {
                return false
            } else if (
                tabItem.value === StudioTabs.WorkItemManager &&
                !isInternalCmsUser
            ) {
                return false
            } else if (tabItem.value === StudioTabs.Report && !isAeUser) {
                return false
            }
            return true
        },
        [isBestPracticeEnabled, isAeUser]
    )
    React.useEffect(() => {
        setActiveStudioHeaderTab(getActiveHeader(pathname))
    }, [pathname])

    return (
        <>
            <GridContainer
                // See ProjectTopTabNav for why Harmony always forces the tight, centered layout.
                spacing={isHarmonyHeader || expandToHorizontalSpace ? 0 : 4}
                alignItems="center"
                data-testid="studioTopTabNav"
                className={clsx(
                    classes.topNavButtons,
                    isHarmonyHeader && classes.topNavButtonsLight
                )}
                ref={containerRef}
                onKeyDown={onKeyDown}
            >
                {TabItems.map((tabItem: StudioTabItemProps, index: number) => {
                    if (!isTabDisplayed(tabItem)) return
                    const isActive = activeStudioHeaderTab === tabItem.value
                    return (
                        <GridItem
                            data-testid={`topTabNavStudio-${tabItem.label}`}
                            key={`tabItem.value-${index}`}
                        >
                            <Button
                                component={NavLink}
                                aria-current={isActive ? 'page' : undefined}
                                to={
                                    !isContentTeamUser &&
                                    !isBestPracticeEnabled &&
                                    typeof tabItem.link === 'object'
                                        ? '/master'
                                        : tabItem.link
                                }
                                className={clsx(
                                    // See useHarmonyTabStyles for why dark/light classes are never combined.
                                    isHarmonyHeader
                                        ? harmonyClasses.tabButtonLight
                                        : classes.tabButton,
                                    useBiggerTabs
                                        ? classes.largeSizeTabs
                                        : classes.normalSizeTabs,
                                    !useBiggerTabs && isHarmonyHeader && classes.normalSizeTabsHarmony,
                                    isActive
                                        ? isHarmonyHeader
                                            ? harmonyClasses.activeTabButtonLight
                                            : classes.activeTabButton
                                        : null
                                )}
                                classes={{
                                    disabled: disabledTabButtonClassName,
                                }}
                                onClick={handleActiveTabSelection}
                            >
                                {tabItem.label}
                            </Button>
                        </GridItem>
                    )
                })}
            </GridContainer>
        </>
    )
}

export default StudioTopTabNav
